import {
  RingFinishChoices,
  type RingFinishData,
} from '@jouzen/ecom-components/v3/RingFinishChoices';
import { SizeChoices } from '@jouzen/ecom-components/v3/SizeChoices';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import {
  findProductDataBySku,
  findSku,
  findVariant,
  isVariantInStock,
} from '@/app/utils';
import { OrderLineItem } from '@/types/Order';
import { Product } from '@/types/Product';

import Note from '../../Note';
import SectionHeading from '../../SectionHeading';
import { SizeGuide } from '../../SizeGuide';
import useProductOptionsData from '../useProductOptionsData';

const INIT = {
  FINISH: '',
  SIZE: '',
};

export interface SingleRingProps {
  readonly className?: string;
  readonly sizeError: string;
  readonly onSelect: (sku: OrderLineItem['sku']) => void;
  readonly products: Product[];
  readonly selectedSku: string | undefined;
}

const SingleRing = ({
  className,
  sizeError,
  selectedSku,
  onSelect,
  products,
}: SingleRingProps) => {
  const t = useTranslations();

  const { finishOptions, sizeOptions } = useProductOptionsData(products, {
    selectedSku,
    imageType: 'selector',
  });

  const { finish: initFinish = INIT.FINISH, size: initSize = INIT.SIZE } =
    findProductDataBySku(products, selectedSku);

  const [selectedFinish, setSelectedFinish] = useState<string>(initFinish);
  const [selectedSize, setSelectedSize] = useState<string>(initSize);

  const handleChange = useCallback(
    (finish: string, size: string) => {
      // fire onSelect when selection changes
      const sku = findSku(products, finish, size);

      if (sku !== selectedSku) {
        onSelect(sku);
      }
    },
    [onSelect, products, selectedSku],
  );

  const handleChangeFinish = useCallback(
    (newFinish: string) => {
      // Don't carry the size over when it's out of stock in the new finish —
      // reset to "size pending" so an out-of-stock SKU is never selected.
      const variant = findVariant(products, newFinish, selectedSize);
      const size = isVariantInStock(variant) ? selectedSize : INIT.SIZE;

      handleChange(newFinish, size);
      setSelectedFinish(newFinish);
      setSelectedSize(size);
    },
    [handleChange, products, selectedSize],
  );

  const handleChangeSize = useCallback(
    (newSize: string) => {
      // SizeChoices renders out-of-stock sizes but doesn't disable them —
      // ignore a pick that would resolve to an out-of-stock SKU.
      const variant = findVariant(products, selectedFinish, newSize);
      const size = isVariantInStock(variant) ? newSize : INIT.SIZE;

      handleChange(selectedFinish, size);
      setSelectedSize(size);
    },
    [handleChange, products, selectedFinish],
  );

  useEffect(() => {
    // set initial finish option (once products load)
    if (
      finishOptions.length &&
      !finishOptions.some(({ value }) => value === selectedFinish)
    ) {
      const finish = finishOptions.some(({ value }) => value === INIT.FINISH)
        ? INIT.FINISH
        : finishOptions[0].value;

      handleChange(finish, selectedSize);
      setSelectedFinish(finish);
    }
  }, [finishOptions, handleChange, selectedFinish, selectedSize]);

  useEffect(() => {
    // reset selected size when not found in the list of options, or when it's
    // out of stock (e.g. a stale saved selection)
    const selectedOption = sizeOptions?.find(
      ({ value }) => value === selectedSize,
    );
    if (!selectedOption?.inStock) {
      handleChange(selectedFinish, INIT.SIZE);
      setSelectedSize(INIT.SIZE);
    }
  }, [handleChange, selectedFinish, selectedSize, sizeOptions]);

  return (
    <div data-testid="product-select-single-ring" className={className}>
      <section className="flex flex-col gap-4">
        <SectionHeading id="finish-options-heading">
          {t('eop_order_selections_page_choose_finish')}
        </SectionHeading>

        {initFinish && ( // hide until initFinish is set (TODO: refactor to force re-render)
          <RingFinishChoices
            data-testid="finish-options"
            initialSelectedFinish={initFinish}
            ringFinishesData={
              finishOptions
                .filter((opt) => Boolean(opt.imgSrc))
                .map((opt) => ({
                  alt: opt.label,
                  color: opt.label,
                  finish: opt.value,
                  src: opt.imgSrc,
                })) as RingFinishData[]
            }
            onChange={handleChangeFinish}
            aria-labelledby="finish-options-heading"
            aria-required="true"
          />
        )}
      </section>

      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div>
            <SectionHeading id="size-options-heading">
              {t('eop_order_selections_page_choose_size')}
            </SectionHeading>

            {sizeError && (
              <Typography color="error" variant="body-small">
                {sizeError}
              </Typography>
            )}
          </div>

          <Note>{t('eop_order_selections_page_choose_size_description')}</Note>

          <SizeGuide />
        </div>

        <SizeChoices
          // Remount when the finish changes: SizeChoices keeps its own
          // selected-size state, which would go stale when a guard above
          // resets ours (e.g. the size is out of stock in the new finish).
          key={selectedFinish}
          data-testid="size-options"
          onChange={handleChangeSize}
          ringSizesData={sizeOptions.map((size) => ({
            size: parseInt(size.value, 10),
            // Only annotate out-of-stock sizes; in-stock entries render with
            // no status, as before.
            stockStatus: size.inStock ? undefined : ('outOfStock' as const),
          }))}
          initialSelectedSize={parseInt(selectedSize, 10)}
          aria-errormessage={sizeError}
          aria-invalid={Boolean(sizeError)}
          aria-required="true"
          aria-labelledby="size-options-heading"
          label={''} // TODO: remove after removing default from ecom-components
        />
      </section>
    </div>
  );
};

export default SingleRing;
